import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Smartphone, Search, Calendar, RefreshCw, User, Building, Truck, 
  Package, Plus, Edit, Trash2, Settings, CheckCircle, XCircle,
  Filter, Download, ChevronLeft, ChevronRight, AlertTriangle
} from 'lucide-react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const SuiviPDA = ({ user }) => {
  const [pdaHistory, setPdaHistory] = useState([]);
  const [pdas, setPdas] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history'); // 'history', 'management'
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('4months'); // 4months, today, week, all
  const [pdaFilter, setPdaFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Toggle to show/hide missions in history
  const [showMissions, setShowMissions] = useState(false);
  
  // PDA Management form
  const [pdaForm, setPdaForm] = useState({ numero: '', description: '', site_id: '' });
  const [editingPda, setEditingPda] = useState(null);

  useEffect(() => {
    fetchData();
  }, [dateFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyRes, pdasRes, sitesRes] = await Promise.all([
        axios.get(`${API}/api/pda-history?date_filter=${dateFilter}`, { withCredentials: true }),
        axios.get(`${API}/api/pdas`, { withCredentials: true }),
        axios.get(`${API}/api/sites`, { withCredentials: true })
      ]);
      
      setPdaHistory(historyRes.data.history || []);
      setPdas(pdasRes.data.pdas || []);
      setSites(sitesRes.data.sites || sitesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPdas = async () => {
    try {
      const res = await axios.get(`${API}/api/pdas`, { withCredentials: true });
      setPdas(res.data.pdas || []);
    } catch (error) {
      console.error('Failed to fetch PDAs:', error);
    }
  };

  const initPdas = async () => {
    try {
      await axios.post(`${API}/api/pdas/init`, {}, { withCredentials: true });
      fetchPdas();
      alert('PDAs initialisés avec succès');
    } catch (error) {
      alert('Erreur lors de l\'initialisation des PDAs');
    }
  };

  const handleAddPda = async () => {
    if (!pdaForm.numero) return;
    try {
      await axios.post(`${API}/api/pdas`, pdaForm, { withCredentials: true });
      setPdaForm({ numero: '', description: '', site_id: '' });
      fetchPdas();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'ajout du PDA');
    }
  };

  const handleUpdatePda = async () => {
    if (!editingPda) return;
    try {
      await axios.put(`${API}/api/pdas/${editingPda.pda_id}`, {
        numero: editingPda.numero,
        description: editingPda.description,
        site_id: editingPda.site_id || '',
        active: editingPda.active
      }, { withCredentials: true });
      setEditingPda(null);
      fetchPdas();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeletePda = async (pdaId) => {
    if (!confirm('Supprimer ce PDA ?')) return;
    try {
      await axios.delete(`${API}/api/pdas/${pdaId}`, { withCredentials: true });
      fetchPdas();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  // Filter history
  const filteredHistory = useMemo(() => {
    let filtered = [...pdaHistory];
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.driver_name?.toLowerCase().includes(search) ||
        item.pda_numero?.toLowerCase().includes(search) ||
        item.vehicle_plate?.toLowerCase().includes(search) ||
        item.site_name?.toLowerCase().includes(search)
      );
    }
    
    // Site filter
    if (siteFilter !== 'all') {
      filtered = filtered.filter(item => item.site_id === siteFilter);
    }
    
    // PDA filter
    if (pdaFilter !== 'all') {
      filtered = filtered.filter(item => item.pda_id === pdaFilter);
    }
    
    // Date filter is now handled server-side via API query parameter
    // No client-side date filtering needed
    
    // Sort by date descending
    filtered.sort((a, b) => b.date.localeCompare(a.date));
    
    return filtered;
  }, [pdaHistory, searchTerm, siteFilter, pdaFilter, dateFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const paginatedHistory = filteredHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get unique PDA count
  const uniquePDAsUsed = useMemo(() => {
    const pdaSet = new Set(filteredHistory.filter(h => h.pda_id).map(h => h.pda_id));
    return pdaSet.size;
  }, [filteredHistory]);

  // Get Colis Privé sites for filtering
  const cpSites = useMemo(() => {
    return sites.filter(s => 
      s.type === 'Colis Privé' || 
      s.name?.includes('Colis Priv') ||
      s.site_type === 'Colis Privé'
    );
  }, [sites]);

  if (loading) {
    return (
      <Layout user={user} activeModule="b2b">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule="b2b">
      <div className="space-y-6" data-testid="suivi-pda-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Smartphone className="w-7 h-7 text-purple-600" />
              Suivi PDA
            </h1>
            <p className="text-slate-500 mt-1">Suivi des terminaux PDA utilisés par les livreurs Colis Privé</p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">PDA Actifs</p>
                  <p className="text-2xl font-bold text-purple-900">{pdas.filter(p => p.active).length}</p>
                </div>
                <Smartphone className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Missions avec PDA</p>
                  <p className="text-2xl font-bold text-blue-900">{filteredHistory.filter(h => h.pda_id).length}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">PDA Utilisés</p>
                  <p className="text-2xl font-bold text-green-900">{uniquePDAsUsed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total Missions</p>
                  <p className="text-2xl font-bold text-slate-900">{filteredHistory.length}</p>
                </div>
                <Calendar className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'history' 
                ? 'text-purple-600 border-b-2 border-purple-500' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-history"
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Historique PDA
          </button>
          <button
            onClick={() => setActiveTab('management')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'management' 
                ? 'text-purple-600 border-b-2 border-purple-500' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-management"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Gestion des PDA
          </button>
        </div>

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher (livreur, PDA, plaque, site)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <select
                value={siteFilter}
                onChange={(e) => { setSiteFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                data-testid="site-filter"
              >
                <option value="all">Tous les sites</option>
                {cpSites.map(s => (
                  <option key={s.site_id} value={s.site_id}>{s.name}</option>
                ))}
              </select>
              <select
                value={pdaFilter}
                onChange={(e) => { setPdaFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                data-testid="pda-filter"
              >
                <option value="all">Tous les PDA</option>
                {pdas.filter(p => p.active).map(p => (
                  <option key={p.pda_id} value={p.pda_id}>{p.numero}</option>
                ))}
              </select>
              <select
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                data-testid="date-filter"
              >
                <option value="4months">4 derniers mois</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="all">Toutes les dates</option>
              </select>
            </div>

            {/* History Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Historique d'utilisation des PDA ({filteredHistory.length} entrées)
                </CardTitle>
                {/* Toggle button for showing missions */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowMissions(!showMissions)}
                  className={showMissions ? 'bg-purple-50 border-purple-300' : ''}
                >
                  {showMissions ? '🔽 Masquer missions' : '▶️ Afficher missions'}
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="pda-history-table">
                    <thead className="bg-slate-50 border-y">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Date</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Livreur</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Site</th>
                        {showMissions && (
                          <th className="text-left p-3 text-xs font-semibold text-slate-600">Mission</th>
                        )}
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Véhicule</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">PDA Utilisé</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paginatedHistory.length === 0 ? (
                        <tr>
                          <td colSpan={showMissions ? 6 : 5} className="p-8 text-center text-slate-500">
                            <Smartphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>Aucune donnée de PDA trouvée pour les filtres sélectionnés</p>
                          </td>
                        </tr>
                      ) : (
                        paginatedHistory.map((item, index) => (
                          <tr key={`${item.schedule_id}-${index}`} className="hover:bg-slate-50">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span className="font-medium text-sm">{formatDate(item.date)}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="text-sm">{item.driver_name || 'Inconnu'}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-purple-400" />
                                <span className="text-sm">{item.site_name || '-'}</span>
                                {item.site_mismatch && (
                                  <span title="Incohérence : PDA rattaché à un autre site" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-600 text-xs font-medium" data-testid={`pda-mismatch-${index}`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    Inter-site
                                  </span>
                                )}
                              </div>
                            </td>
                            {showMissions && (
                              <td className="p-3">
                                <div className="text-sm">
                                  {item.delivered !== null || item.returned !== null || item.picked !== null ? (
                                    <div className="flex flex-wrap gap-1">
                                      {item.delivered !== null && (
                                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                          {item.delivered} livrés
                                        </Badge>
                                      )}
                                      {item.returned !== null && (
                                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                          {item.returned} retours
                                        </Badge>
                                      )}
                                      {item.picked !== null && (
                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                          {item.picked} ramassés
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">Mission CP</span>
                                  )}
                                </div>
                              </td>
                            )}
                            <td className="p-3">
                              {item.vehicle_plate ? (
                                <span className="font-mono bg-slate-100 px-2 py-1 rounded text-sm">
                                  {item.vehicle_plate}
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {item.pda_numero ? (
                                <Badge className="bg-purple-100 text-purple-700 border border-purple-200">
                                  <Smartphone className="w-3 h-3 mr-1" />
                                  {item.pda_numero}
                                </Badge>
                              ) : (
                                <span className="text-slate-400 text-sm">Non renseigné</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-slate-500">
                      Page {currentPage} sur {totalPages} ({filteredHistory.length} résultats)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Suivant
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Management Tab */}
        {activeTab === 'management' && (
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5 text-slate-500" />
                    Gestion des PDA
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={initPdas} data-testid="init-pdas-btn">
                      Initialiser les PDA par défaut
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Add PDA Form */}
                <div className="flex flex-wrap gap-3 mb-6 p-4 bg-slate-50 rounded-lg">
                  <Input
                    placeholder="Numéro du PDA (ex: 013545A)"
                    value={pdaForm.numero}
                    onChange={(e) => setPdaForm({ ...pdaForm, numero: e.target.value })}
                    className="w-48"
                    data-testid="pda-numero-input"
                  />
                  <Input
                    placeholder="Description (optionnel)"
                    value={pdaForm.description}
                    onChange={(e) => setPdaForm({ ...pdaForm, description: e.target.value })}
                    className="w-56"
                    data-testid="pda-description-input"
                  />
                  <select
                    value={pdaForm.site_id || ''}
                    onChange={(e) => setPdaForm({ ...pdaForm, site_id: e.target.value })}
                    className="px-3 py-2 border rounded-md bg-white text-sm w-52"
                    data-testid="pda-site-select"
                  >
                    <option value="">Site (optionnel)</option>
                    {sites.map(site => (
                      <option key={site.site_id} value={site.site_id}>{site.name}</option>
                    ))}
                  </select>
                  <Button onClick={handleAddPda} disabled={!pdaForm.numero} data-testid="add-pda-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>

                {/* PDA List */}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="pda-management-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Numéro</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Description</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Site</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Statut</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pdas.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-500">
                            <Smartphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>Aucun PDA configuré. Cliquez sur "Initialiser les PDA par défaut" pour commencer.</p>
                          </td>
                        </tr>
                      ) : pdas.map(pda => (
                        <tr key={pda.pda_id} className="hover:bg-slate-50">
                          <td className="p-3">
                            {editingPda?.pda_id === pda.pda_id ? (
                              <Input
                                value={editingPda.numero}
                                onChange={(e) => setEditingPda({ ...editingPda, numero: e.target.value })}
                                className="w-32"
                              />
                            ) : (
                              <span className="font-mono font-semibold">{pda.numero}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingPda?.pda_id === pda.pda_id ? (
                              <Input
                                value={editingPda.description || ''}
                                onChange={(e) => setEditingPda({ ...editingPda, description: e.target.value })}
                                className="w-40"
                              />
                            ) : (
                              <span className="text-slate-600">{pda.description || '-'}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingPda?.pda_id === pda.pda_id ? (
                              <select
                                value={editingPda.site_id || ''}
                                onChange={(e) => setEditingPda({ ...editingPda, site_id: e.target.value })}
                                className="px-2 py-1 border rounded text-sm w-40"
                              >
                                <option value="">Aucun site</option>
                                {sites.map(site => (
                                  <option key={site.site_id} value={site.site_id}>{site.name}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`text-sm ${pda.site_id ? 'text-blue-600 font-medium' : 'text-slate-400'}`}>
                                {pda.site_id ? sites.find(s => s.site_id === pda.site_id)?.name || pda.site_id : 'Non affecté'}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {editingPda?.pda_id === pda.pda_id ? (
                              <select
                                value={editingPda.active ? 'active' : 'inactive'}
                                onChange={(e) => setEditingPda({ ...editingPda, active: e.target.value === 'active' })}
                                className="px-2 py-1 border rounded text-sm"
                              >
                                <option value="active">Actif</option>
                                <option value="inactive">Inactif</option>
                              </select>
                            ) : (
                              <span className={`px-2 py-1 rounded-full text-xs ${pda.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {pda.active ? 'Actif' : 'Inactif'}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {editingPda?.pda_id === pda.pda_id ? (
                              <div className="flex gap-2 justify-center">
                                <Button size="sm" onClick={handleUpdatePda} className="bg-green-600 hover:bg-green-700">
                                  Sauvegarder
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingPda(null)}>
                                  Annuler
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-2 justify-center">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => setEditingPda({ ...pda })}
                                  data-testid={`edit-pda-${pda.pda_id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600 border-red-200 hover:bg-red-50" 
                                  onClick={() => handleDeletePda(pda.pda_id)}
                                  data-testid={`delete-pda-${pda.pda_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SuiviPDA;
